import { useEffect, useState } from 'preact/hooks';
import { api } from '../../api/client';
import type { GwtagWalletDto } from '../../api/types';
import { ErrorTile, Eyebrow } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { useTranslation } from '../../i18n';

/**
 * Gwtag wallet section of /me/account. Shows the user's current
 * marker plus up to two reserved markers (per
 * <see cref="GwtagController.MaxReservedPerUser"/>). Three actions:
 *
 *   • Suggest - fetch a random unique value from the minter without
 *     persisting it (preview).
 *   • Rotate - set a specific value as current. Demotes the previous
 *     current to the reserved stack.
 *   • Release reserved - drop a reserved row, returning the value to
 *     the global pool.
 *
 * <p>Gwtag values are deliberately public per legacy ADR-0022; the
 * attribution model rests on claim-suggestion confirmation, not on
 * value secrecy.</p>
 */
export function AccountGwtagsPage() {
    const t = useTranslation();
    const [wallet, setWallet] = useState<GwtagWalletDto | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState<string | null>(null);
    const toast = useToast();

    const refresh = async () => {
        try {
            const w = await api.me.gwtag.get();
            setWallet(w);
            setLoadError(null);
        } catch (e) {
            setLoadError(e instanceof Error ? e.message : String(e));
        }
    };

    useEffect(() => {
        void refresh();
    }, []);

    const onSuggest = async () => {
        setBusy('suggest');
        try {
            const r = await api.me.gwtag.suggest();
            setInput(r.value);
            toast.push(
                'success',
                <>
                    {t('me.gwtags.rotate.suggestSuccess', { value: r.value })}
                </>,
            );
        } catch (e) {
            toast.push('error', t('me.gwtags.rotate.suggestError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusy(null);
        }
    };

    const onRotate = async () => {
        const value = input.trim();
        if (!value) return;
        setBusy('rotate');
        try {
            const next = await api.me.gwtag.set(value);
            setWallet(next);
            setInput('');
            toast.push('success', <>{t('me.gwtags.rotate.rotateSuccess', { value: next.current.value })}</>);
        } catch (e) {
            toast.push('error', t('me.gwtags.rotate.rotateError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusy(null);
        }
    };

    const onRelease = async (value: string) => {
        setBusy(`release:${value}`);
        try {
            await api.me.gwtag.releaseReserved(value);
            await refresh();
            toast.push('success', <>{t('me.gwtags.reserved.releaseSuccess', { value })}</>);
        } catch (e) {
            toast.push('error', t('me.gwtags.reserved.releaseError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusy(null);
        }
    };

    return (
        <AccountShell
            title={t('me.gwtags.title')}
            subtitle={t('me.gwtags.subtitle')}
        >
            <div class="mx-auto w-full max-w-2xl space-y-8 px-[clamp(20px,3.5vw,56px)] py-10">
                {loadError && (
                    <ErrorTile title={t('me.gwtags.loadError')} details={loadError} />
                )}
                {!wallet && !loadError && (
                    <p class="text-sm text-fg-muted">{t('me.gwtags.loading')}</p>
                )}
                {wallet && (
                    <>
                        <section>
                            <Eyebrow class="mb-3">{t('me.gwtags.current.eyebrow')}</Eyebrow>
                            {wallet.current.value ? (
                                <div class="rounded border border-accent bg-accent-glow/50 p-4">
                                    <code class="block font-mono text-lg text-fg-primary">
                                        {wallet.current.value}
                                    </code>
                                    <p class="mt-2 text-xs text-fg-muted">
                                        {t('me.gwtags.current.created')}{' '}
                                        <time>
                                            {new Date(
                                                wallet.current.createdAt,
                                            ).toLocaleString()}
                                        </time>
                                        {wallet.current.lastDetectedAt && (
                                            <>
                                                {' · '}{t('me.gwtags.current.lastDetected')}{' '}
                                                <time>
                                                    {new Date(
                                                        wallet.current.lastDetectedAt,
                                                    ).toLocaleString()}
                                                </time>
                                                {wallet.current.lastDetectedOnSource && (
                                                    <> {t('me.gwtags.current.on')} {wallet.current.lastDetectedOnSource}</>
                                                )}
                                            </>
                                        )}
                                    </p>
                                </div>
                            ) : (
                                <p class="text-sm text-fg-muted">
                                    {t('me.gwtags.current.empty')}
                                </p>
                            )}
                        </section>

                        <section>
                            <Eyebrow class="mb-3">{t('me.gwtags.rotate.eyebrow')}</Eyebrow>
                            <p class="mb-3 text-sm text-fg-muted">
                                {t('me.gwtags.rotate.body')}
                            </p>
                            <div class="flex flex-wrap items-center gap-2">
                                <input
                                    type="text"
                                    value={input}
                                    onInput={(e) =>
                                        setInput((e.target as HTMLInputElement).value)
                                    }
                                    placeholder={t('me.gwtags.rotate.placeholder')}
                                    class="flex-1 min-w-[240px] rounded border border-rule bg-surface-raised px-3 py-1.5 font-mono text-sm text-fg-primary outline-none focus:border-accent"
                                />
                                <button
                                    type="button"
                                    onClick={onSuggest}
                                    disabled={busy !== null}
                                    class="rounded border border-rule-strong px-3 py-1.5 text-sm text-fg-primary transition-colors hover:border-accent disabled:opacity-50"
                                >
                                    {busy === 'suggest' ? t('me.gwtags.rotate.suggesting') : t('me.gwtags.rotate.suggestCta')}
                                </button>
                                <button
                                    type="button"
                                    onClick={onRotate}
                                    disabled={busy !== null || !input.trim()}
                                    class="rounded border border-accent bg-accent px-4 py-1.5 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary disabled:opacity-50"
                                >
                                    {busy === 'rotate' ? t('me.gwtags.rotate.rotating') : t('me.gwtags.rotate.rotateCta')}
                                </button>
                            </div>
                        </section>

                        <section>
                            <Eyebrow class="mb-3">
                                {t('me.gwtags.reserved.eyebrow')}{' '}
                                <span class="text-fg-faint">
                                    ({wallet.reserved.length} / 2)
                                </span>
                            </Eyebrow>
                            <p class="mb-3 text-sm text-fg-muted">
                                {t('me.gwtags.reserved.body')}
                            </p>
                            {wallet.reserved.length === 0 ? (
                                <p class="text-sm text-fg-muted">{t('me.gwtags.reserved.empty')}</p>
                            ) : (
                                <ul class="space-y-2">
                                    {wallet.reserved.map((r) => (
                                        <li
                                            key={r.id}
                                            class="flex items-center justify-between gap-3 rounded border border-rule bg-surface-raised px-3 py-2"
                                        >
                                            <div class="min-w-0">
                                                <code class="block font-mono text-sm text-fg-primary">
                                                    {r.value}
                                                </code>
                                                {r.retiredAt && (
                                                    <span class="text-xs text-fg-muted">
                                                        {t('me.gwtags.reserved.retired')}{' '}
                                                        <time>
                                                            {new Date(
                                                                r.retiredAt,
                                                            ).toLocaleString()}
                                                        </time>
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => onRelease(r.value)}
                                                disabled={busy !== null}
                                                class="rounded border border-rule-strong px-2 py-1 text-xs text-fg-muted transition-colors hover:border-state-failed hover:text-state-failed disabled:opacity-50"
                                            >
                                                {busy === `release:${r.value}`
                                                    ? t('me.gwtags.reserved.releasing')
                                                    : t('me.gwtags.reserved.releaseCta')}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </>
                )}
            </div>
        </AccountShell>
    );
}
